<template>
  <v-menu
    v-if="loading || (revisionContext && revisionContext.has_revisions)"
    offset-y
    left
  >
    <template v-slot:activator="{ on, attrs }">
      <v-tooltip top>
        <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <v-badge
            :value="!loading && revisionCount > 0"
            :content="revisionCount"
            color="white"
            class="revision-selector-badge"
            bordered
          >
            <v-btn
              icon
              small
              color="white"
              v-bind="{ ...attrs, ...tooltipAttrs }"
              v-on="{ ...on, ...tooltipOn }"
            >
              <v-progress-circular
                v-if="loading"
                indeterminate
                :size="18"
                :width="2"
                color="white"
              />
              <v-icon v-else>mdi-history</v-icon>
            </v-btn>
          </v-badge>
        </template>
        <span>Revisiones</span>
      </v-tooltip>
    </template>

    <v-card class="revision-menu" elevation="8">
      <div class="revision-menu-header">
        <div class="revision-menu-icon">
          <v-icon color="primary">mdi-history</v-icon>
        </div>
        <div class="revision-menu-heading">
          <div class="revision-menu-title">Historial de revisiones</div>
          <div class="revision-menu-subtitle">
            {{ revisionCount ? `${revisionCount} revision${revisionCount === 1 ? '' : 'es'} disponible${revisionCount === 1 ? '' : 's'}` : 'Selecciona una versión' }}
          </div>
        </div>
      </div>

      <v-divider />

      <v-list dense class="revision-menu-list">
        <v-list-item v-if="loading" class="revision-loading-row" disabled>
          <v-list-item-icon>
            <v-progress-circular indeterminate :size="18" :width="2" color="primary" />
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Cargando revisiones...</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-for="option in options"
          :key="option.key"
          :class="{ 'revision-option-current': option.file.id === currentFileId }"
          :disabled="option.file.id === currentFileId"
          @click="$emit('select', option.file)"
        >
          <v-list-item-icon class="revision-option-icon">
            <v-icon :color="option.file.id === currentFileId ? 'primary' : 'grey darken-1'">
              {{ option.icon }}
            </v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title class="revision-option-title">
              {{ option.title }}
            </v-list-item-title>
            <v-list-item-subtitle class="revision-option-subtitle">
              <span class="revision-option-name">{{ option.file.name }}</span>
              <span v-if="option.subtitle"> · {{ option.subtitle }}</span>
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-list-item-action v-if="option.file.id === currentFileId">
            <v-chip x-small outlined color="primary" label>Actual</v-chip>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  name: 'RevisionFileSelector',
  props: {
    revisionContext: {
      type: Object,
      default: null
    },
    currentFileId: {
      type: [Number, String],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    revisionCount() {
      return this.revisionContext?.revisions?.length || 0
    },
    options() {
      if (!this.revisionContext) return []

      const options = []
      if (this.revisionContext.main_file) {
        options.push({
          key: `original-${this.revisionContext.main_file.id}`,
          file: this.revisionContext.main_file,
          title: 'Archivo original',
          icon: 'mdi-file-outline',
          subtitle: this.formatDate(this.revisionContext.main_file.created_at)
        })
      }

      const revisions = this.revisionContext.revisions || []
      revisions.forEach((revision, index) => {
        if (!revision.file) return
        options.push({
          key: `revision-${revision.id || index}`,
          file: revision.file,
          title: this.getRevisionTitle(revision, index),
          icon: 'mdi-file-document-edit-outline',
          subtitle: this.getRevisionSubtitle(revision)
        })
      })

      return options
    }
  },
  methods: {
    getRevisionTitle(revision, index) {
      const version = revision.info?.version
      return version ? `Revision ${version}` : `Revision ${index + 1}`
    },
    getRevisionSubtitle(revision) {
      const infoDate = revision.info?.info_date || revision.created_at
      return this.formatDate(infoDate)
    },
    formatDate(value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.revision-selector-badge /deep/ .v-badge__badge {
  top: -2px !important;
  bottom: auto !important;
  left: calc(100% - 6px) !important;
  right: auto !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  color: #2D4B95 !important;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
}

.revision-menu {
  width: 340px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
}

.revision-menu-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px 12px;
  background: #F4F6FB;
}

.revision-menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #E8ECF5;
}

.revision-menu-heading {
  min-width: 0;
}

.revision-menu-title {
  color: #18243D;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
}

.revision-menu-subtitle {
  margin-top: 3px;
  color: #576080;
  font-size: 12px;
  line-height: 1.25;
}

.revision-menu-list {
  max-height: min(420px, calc(100vh - 160px));
  overflow-y: auto;
  padding: 6px;
}

.revision-menu-list .v-list-item {
  min-height: 56px;
  margin: 2px 0;
  border-radius: 6px;
}

.revision-menu-list .v-list-item:not(.v-list-item--disabled):hover {
  background: #F4F6FB;
}

.revision-menu-list .revision-option-current {
  background: #EEF1F8;
}

.revision-option-icon {
  align-self: center;
  min-width: 34px;
  margin: 0 8px 0 2px;
}

.revision-option-title {
  color: #18243D;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.25;
}

.revision-option-subtitle {
  display: block;
  max-width: 205px;
  overflow: hidden;
  color: #576080 !important;
  font-size: 11px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.revision-option-name {
  color: #576080;
}

.revision-menu-list .v-list-item__action {
  margin-left: 8px;
}

.revision-loading-row {
  min-height: 50px !important;
}
</style>
